import React, { useState, useEffect } from 'react';
import { User, Category } from '../../types';
import { fetchAllProfiles, adminUpdateUserCategories } from '../../services/dataService';

interface AdminUsersProps {
  categories: Category[];
}

const AdminUsers: React.FC<AdminUsersProps> = ({ categories }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userAllowedCats, setUserAllowedCats] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await fetchAllProfiles();
      setUsers(data);
    } catch (e) {
      console.error('Failed to load users', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setUserAllowedCats(user.allowedCategories || []);
  };

  const handleToggleCategory = (catId: string) => {
    setUserAllowedCats(prev =>
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    setIsSaving(true);
    try {
      const success = await adminUpdateUserCategories(selectedUser.id, userAllowedCats);
      if (success) {
        setUsers(prev =>
          prev.map(u => (u.id === selectedUser.id ? { ...u, allowedCategories: userAllowedCats } : u))
        );
        setSelectedUser(null);
        alert('Permissions updated successfully!');
      } else {
        alert('Failed to update permissions.');
      }
    } catch (e) {
      console.error(e);
      alert('An error occurred.');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      (user.name || '').toLowerCase().includes(query) ||
      (user.email || '').toLowerCase().includes(query) ||
      (user.targetExam || '').toLowerCase().includes(query) ||
      (user.phone || '').toLowerCase().includes(query)
    );
  });

  return (
    <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-glass overflow-hidden animate-fade-in w-full">
      {/* Tab Header */}
      <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-50/55 dark:bg-slate-800/50">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center">
            <i className="fas fa-users-cog text-orange-500 mr-2.5"></i>
            User Access Management
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            Configure target category permissions for general users to restrict home dashboard visibility.
          </p>
        </div>
        <div className="relative w-full md:w-80">
          <i className="fas fa-search absolute left-3.5 top-3 text-slate-400"></i>
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-100 text-sm"
          />
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="p-12 text-center text-slate-500 dark:text-slate-400">
          <i className="fas fa-spinner fa-spin text-3xl mb-4 text-orange-500"></i>
          <p>Loading users profile details...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="p-12 text-center text-slate-500 dark:text-slate-400">
          <i className="fas fa-user-slash text-4xl mb-4 text-slate-350 dark:text-slate-600"></i>
          <p className="font-semibold text-lg text-slate-700 dark:text-slate-300">No users found</p>
          <p className="text-sm mt-1">Try refining your search query.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900 border-b border-slate-250 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-xs font-bold uppercase tracking-wider">
                <th className="px-6 py-4">User</th>
                <th className="px-6 py-4">Phone / Target Exam</th>
                <th className="px-6 py-4">Role & Tier</th>
                <th className="px-6 py-4">Allowed Categories</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
              {filteredUsers.map(user => {
                const userInitials = (user.name || user.email || 'U')[0].toUpperCase();
                return (
                  <tr key={user.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/20 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 flex items-center justify-center font-bold text-sm shadow-sm">
                          {userInitials}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 dark:text-white text-sm">
                            {user.name || 'Anonymous User'}
                          </div>
                          <div className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-700 dark:text-slate-300">
                        {user.phone || 'No phone'}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-medium">
                        Target: {user.targetExam || 'Not chosen'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1.5 items-center">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                          user.role === 'admin' 
                            ? 'bg-red-50 dark:bg-red-950/30 text-red-650 dark:text-red-400 border border-red-100 dark:border-red-900/30' 
                            : 'bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/30'
                        }`}>
                          {user.role}
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                          user.subscriptionTier === 'premium'
                            ? 'bg-purple-100 dark:bg-purple-950/45 text-purple-700 dark:text-purple-300 border border-purple-200'
                            : user.subscriptionTier === 'pro'
                              ? 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border border-amber-100'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                        }`}>
                          {user.subscriptionTier || 'free'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {user.role === 'admin' ? (
                        <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 italic">
                          All Categories (Admin)
                        </span>
                      ) : user.allowedCategories && user.allowedCategories.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {user.allowedCategories.map(catId => {
                            const cat = categories.find(c => c.id === catId);
                            return (
                              <span
                                key={catId}
                                className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs px-2.5 py-1 rounded-lg font-medium flex items-center gap-1 shadow-sm"
                              >
                                {cat ? (
                                  <>
                                    <i className={`fas ${cat.icon} text-orange-500 text-[10px]`}></i>
                                    {cat.name}
                                  </>
                                ) : (
                                  catId
                                )}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-xs font-semibold text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-950/20 px-2 py-1 rounded-lg">
                          No category selected
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {user.role === 'admin' ? (
                        <span className="text-slate-400 dark:text-slate-500 text-xs italic">System Admin</span>
                      ) : (
                        <button
                          onClick={() => handleEditClick(user)}
                          className="px-3 py-1.5 bg-orange-50 hover:bg-orange-500 dark:bg-orange-950/20 text-orange-600 dark:text-orange-400 hover:text-white rounded-lg font-semibold text-xs border border-orange-200/50 hover:border-orange-500 transition-all flex items-center gap-1.5 ml-auto shadow-sm"
                        >
                          <i className="fas fa-user-lock"></i>
                          Edit Access
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Permissions Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-glass-lg w-full max-w-lg overflow-hidden border border-slate-200 dark:border-slate-700 animate-scale-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
              <h3 className="text-xl font-bold flex items-center">
                <i className="fas fa-user-shield mr-2.5"></i>
                Category Permissions
              </h3>
              <p className="text-orange-100 text-xs mt-1">
                Configure access for <strong>{selectedUser.name || selectedUser.email}</strong>
              </p>
            </div>

            {/* Checkboxes List */}
            <div className="p-6 space-y-4">
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                Check the exam categories you want this user to access. General users can only see categories selected below.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[300px] overflow-y-auto pr-1">
                {categories.map(cat => {
                  const isChecked = userAllowedCats.includes(cat.id);
                  return (
                    <div
                      key={cat.id}
                      onClick={() => handleToggleCategory(cat.id)}
                      className={`p-3 rounded-2xl border-2 cursor-pointer transition-all flex items-center justify-between group ${
                        isChecked
                          ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-950/20'
                          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-orange-200 dark:hover:border-orange-900/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm shadow-sm transition-all ${
                          isChecked
                            ? 'bg-orange-500 text-white'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-orange-100 group-hover:text-orange-650'
                        }`}>
                          <i className={`fas ${cat.icon}`}></i>
                        </div>
                        <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                          {cat.name}
                        </span>
                      </div>
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                        isChecked
                          ? 'border-orange-500 bg-orange-500 text-white'
                          : 'border-slate-300 dark:border-slate-650 bg-transparent'
                      }`}>
                        {isChecked && <i className="fas fa-check text-[10px]"></i>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-700/60 flex justify-end gap-3">
              <button
                onClick={() => setSelectedUser(null)}
                disabled={isSaving}
                className="px-4 py-2 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold text-sm rounded-xl hover:bg-slate-100 dark:hover:bg-slate-850 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePermissions}
                disabled={isSaving}
                className="px-5 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-sm rounded-xl hover:opacity-95 shadow-md flex items-center disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i> Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i> Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
